import { useEffect, useRef, useState } from "react";
import { Check, CheckCircle2, Plus } from "lucide-react";
import { useSimplAI } from "../../../imports/useSimpleAI";
import { fetchLatestPoMismatchAnalysis } from "../../../services/POMismatchService";
import { Ticket } from "./types";

type AgentState = "inactive" | "running" | "resolved" | "sent";

const FEED_LINES = [
    { text: "📄 Gathering purchase order, goods receipt and invoice details...", warn: false },
    { text: "🤖 AI analysis has started. Reviewing procurement documents...", warn: false },
];

type FeedLine = { time: string; text: string; warn: boolean };

const getFeedTime = (offsetSeconds = 0) => new Date(Date.now() + offsetSeconds * 1000).toLocaleTimeString("en-US", { hour12: false });
const createInitialFeedLines = (): FeedLine[] => FEED_LINES.map((line, index) => ({ ...line, time: getFeedTime(index) }));
const MIN_TRACKING_DURATION_MS = 7000;
const FEED_LINE_INTERVAL_MS = 900;

async function waitForMinimumTrackingDuration(startedAt: number) {
    const remaining = MIN_TRACKING_DURATION_MS - (Date.now() - startedAt);
    if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
    }
}

const WORKFLOW_STEPS = [
    { label: "Ticket intake", running: "classified as price mismatch", done: "classified as price mismatch" },
    { label: "Ariba API pull", running: "3 PO lines, 2 GR entries retrieved", done: "3 PO lines, 2 GR entries retrieved" },
    { label: "Receipt & contract check", running: "Comparing invoice line 1 against contract CTR-2211...", done: "GRN quantities and contract price list validated against the invoice." },
    { label: "Mismatch diagnosis", running: "~15 sec after step 3", done: "Line-level delta computed and ranked by cash impact." },
    { label: "Resolution drafting", running: "queued", done: "Resolution options generated with confidence and a ready-to-paste ticket reply." },
];

type MismatchAnalysis = {
    dataTable: Array<Record<string, string | number | null>>;
    summary: string;
    exposure: number;
    severity: string;
    documentReferences: {
        ticket_id?: string;
        pr_number?: string;
        po_number?: string;
        grn_number?: string;
        invoice_number?: string;
        supplier?: string;
        supplier_code?: string;
        priority?: string;
    };
    recommendations: Array<{ line_number: number; item_description: string; recommendation: string; recommended_action: string; financial_exposure: number }>;
    overallRecommendation?: string;
    recommendedActions?: string;
};

function normalizeAnalysis(response: any): MismatchAnalysis | null {
    let output = response;

    // SimplAI can wrap the final payload in multiple data/output envelopes.
    for (let depth = 0; depth < 5; depth += 1) {
        if (Array.isArray(output?.Data_table) && output?.rest_items) break;
        if (output?.output && typeof output.output === "object") {
            output = output.output;
        } else if (output?.data && typeof output.data === "object") {
            output = output.data;
        } else {
            break;
        }
    }

    const rows = output?.Data_table;
    const restItems = output?.rest_items;
    if (!Array.isArray(rows) || !restItems) return null;
    return {
        dataTable: rows,
        summary: String(output.AI_Summary || ""),
        exposure: Number(restItems.financial_exposure || 0),
        severity: String(restItems.severity || ""),
        documentReferences: restItems.document_references || {},
        recommendations: Array.isArray(restItems.item_resolutions) ? restItems.item_resolutions : [],
        overallRecommendation: String(restItems.overall_recommendation || ""),
        recommendedActions: String(output.AI_Summary || ""),
    };
}

function formatComparisonValue(value: string | number | null) {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "number") return value.toLocaleString();
    return String(value);
}

function formatExposure(value: string | number | null | undefined) {
    const amount = Number(value || 0);
    return Number.isFinite(amount) ? amount.toLocaleString() : "0";
}

function getComparisonRows(dataTable: MismatchAnalysis["dataTable"]) {
    return dataTable.map((row) => {
        const isMatch = row.status === "Match";
        const result = isMatch
            ? "Match"
            : String(row.mismatch_description || row.status || "Mismatch");

        return [
            String(row.attribute || "Line item"),
            formatComparisonValue(row.po_value),
            formatComparisonValue(row.invoice_value),
            result,
            isMatch ? "match" : "over",
        ] as const;
    });
}

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_BASE_URL || ""
    : process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
    
async function fetchMismatchInputData(): Promise<Record<string, string>> {
    const response = await fetch(`${API_BASE_URL}/api/po-mismatch/backend-data`, {
        credentials: "include",
    });

    if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.message || "Could not load document input JSON");
    }

    const inputData = await response.json();
    const requiredKeys = ["po_file_url", "grn_file_url", "invoice_file_url", "pr_file_url"];

    if (!inputData || typeof inputData !== "object" || requiredKeys.some((key) => typeof inputData[key] !== "string" || !inputData[key])) {
        throw new Error("Stored document input JSON is incomplete");
    }

    return inputData;
}

async function persistMismatchAnalysis({ ticketId, inputData, outputData, conversationId, messageId, traceId }: {
    ticketId: string;
    inputData: Record<string, string>;
    outputData: unknown;
    conversationId: string;
    messageId: string;
    traceId?: string | null;
}) {
    const response = await fetch(`${API_BASE_URL}/api/po-mismatch-analysis`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            ticket_id: ticketId,
            conversation_id: conversationId,
            message_id: messageId,
            trace_id: traceId || null,
            input_data: inputData,
            output_data: outputData,
            processing_status: "SUCCESS",
        }),
    });

    if (!response.ok) throw new Error("Could not save mismatch analysis");
}

export function ResolutionAgentTab({ selectedTicket, onBackToQueue, onViewInsights }: { selectedTicket: Ticket; onBackToQueue: () => void; onViewInsights: () => void }) {
    const [agentState, setAgentState] = useState<AgentState>("inactive");
    const [workflowOpen, setWorkflowOpen] = useState(true);
    const [analysis, setAnalysis] = useState<MismatchAnalysis | null>(null);
    const [analysisError, setAnalysisError] = useState<string | null>(null);
    const [feedLines, setFeedLines] = useState<FeedLine[]>([]);
    const [visibleFeedLineCount, setVisibleFeedLineCount] = useState(0);
    const visibleFeedLineCountRef = useRef(0);
    const feedLineCountRef = useRef(0);
    const [isPolling, setIsPolling] = useState(false);
    const [isCheckingSavedAnalysis, setIsCheckingSavedAnalysis] = useState(false);
    const { runSimplAI, loading: isAnalysisLoading } = useSimplAI();

    const resetFeedLines = (lines: FeedLine[]) => {
        feedLineCountRef.current = lines.length;
        visibleFeedLineCountRef.current = 0;
        setVisibleFeedLineCount(0);
        setFeedLines(lines);
    };

    const appendFeedLines = (lines: FeedLine[]) => {
        feedLineCountRef.current += lines.length;
        setFeedLines((current) => [...current, ...lines]);
    };

    const waitForFeedPlayback = async () => {
        while (visibleFeedLineCountRef.current < feedLineCountRef.current) {
            await new Promise((resolve) => setTimeout(resolve, 50));
        }
    };

    useEffect(() => {
        setAgentState("inactive");
        setWorkflowOpen(true);
        setAnalysis(null);
        setAnalysisError(null);
        resetFeedLines([]);
        setIsPolling(false);
        setIsCheckingSavedAnalysis(false);
    }, [selectedTicket.id]);

    useEffect(() => {
        if (agentState !== "running" || visibleFeedLineCount >= feedLineCountRef.current) return;

        const timer = setTimeout(() => {
            setVisibleFeedLineCount((current) => {
                const nextCount = Math.min(current + 1, feedLineCountRef.current);
                visibleFeedLineCountRef.current = nextCount;
                return nextCount;
            });
        }, FEED_LINE_INTERVAL_MS);

        return () => clearTimeout(timer);
    }, [agentState, feedLines.length, visibleFeedLineCount]);

    const workflowStatus = (index: number) => agentState === "inactive" ? "queued" : agentState === "running" ? index < 2 ? "done" : index === 2 ? "active" : "queued" : "done";

    const runAgent = async (trackingStartedAt: number) => {
        setAgentState("running");
        setWorkflowOpen(true);
        setAnalysisError(null);
        resetFeedLines(createInitialFeedLines());
        setIsPolling(false);

        try {
            const inputData = await fetchMismatchInputData();
            let conversationId = "";
            let messageId = "";
            const response = await runSimplAI({
                data: {
                    input_data: inputData,
                },
            }, 55, {
                onPostResponse: ({ cId, mId }) => {
                    conversationId = cId;
                    messageId = mId;
                    setIsPolling(true);
                    const nextFeedLineIndex = feedLineCountRef.current;
                    appendFeedLines([{
                        time: getFeedTime(nextFeedLineIndex),
                        text: `✅ Analysis request received successfully.`,
                        warn: false,
                    }, {
                        time: getFeedTime(nextFeedLineIndex + 1),
                        text: "🔍 Comparing invoice against PO, GRN and contract data....",
                        warn: false,
                    }]);
                },
                onPollComplete: ({ data, trace_id }) => {
                    setIsPolling(false);
                    const responseData = data && typeof data === "object" ? data : {};
                    const responseRows = responseData?.output?.Data_table || responseData?.Data_table || [];
                    const nextFeedLineIndex = feedLineCountRef.current;
                    appendFeedLines([
                        { time: getFeedTime(nextFeedLineIndex), text: "GET complete - mismatch analysis response received", warn: false },
                        { time: getFeedTime(nextFeedLineIndex + 1), text: `Response parsed - ${responseRows.length} analysis rows received${trace_id ? ` (trace: ${trace_id})` : ""}`, warn: false },
                    ]);
                },
            });
            const normalized = normalizeAnalysis(response);
            if (!normalized) throw new Error("Unexpected mismatch analysis response");
            let finalAnalysis = normalized;
            try {
                await persistMismatchAnalysis({
                    ticketId: selectedTicket.id,
                    inputData,
                    outputData: response.data,
                    conversationId,
                    messageId,
                    traceId: response.trace_id,
                });
            } catch (persistError) {
                console.error("Could not persist PO mismatch analysis:", persistError);
            }
            try {
                const savedAnalysis = await fetchLatestPoMismatchAnalysis(selectedTicket.id);
                if (savedAnalysis?.ticket_id === selectedTicket.id) {
                    finalAnalysis = normalizeAnalysis(savedAnalysis.output_data) || normalized;
                }
            } catch (fetchError) {
                console.error("Could not reload latest PO mismatch analysis:", fetchError);
            }
            const completionLineIndex = feedLineCountRef.current;
            appendFeedLines([{
                time: getFeedTime(completionLineIndex),
                text: "Agent run complete - response bound to the Resolution Agent view",
                warn: false,
            }]);
            await Promise.all([
                waitForMinimumTrackingDuration(trackingStartedAt),
                waitForFeedPlayback(),
            ]);
            setAnalysis(finalAnalysis);
            setAgentState("resolved");
            setWorkflowOpen(false);
        } catch (error) {
            const warningLineIndex = feedLineCountRef.current;
            appendFeedLines([{
                time: getFeedTime(warningLineIndex),
                text: `Agent run failed - ${error instanceof Error ? error.message : "API response could not be loaded"}`,
                warn: true,
            }]);
            await Promise.all([
                waitForMinimumTrackingDuration(trackingStartedAt),
                waitForFeedPlayback(),
            ]);
            setAnalysisError(error instanceof Error ? error.message : "Live analysis unavailable. Showing the standard diagnosis.");
            setAgentState("resolved");
            setWorkflowOpen(false);
        }
    };

    const handleRunAgent = async () => {
        const trackingStartedAt = Date.now();
        setAgentState("running");
        setWorkflowOpen(true);
        resetFeedLines(createInitialFeedLines());
        setIsPolling(false);
        setIsCheckingSavedAnalysis(true);
        setAnalysisError(null);

        try {
            const savedAnalysis = await fetchLatestPoMismatchAnalysis(selectedTicket.id);
            if (savedAnalysis) {
                const normalized = normalizeAnalysis(savedAnalysis.output_data);
                if (normalized && savedAnalysis.processing_status.toLowerCase() === "success") {
                    const completionLineIndex = feedLineCountRef.current;
                    appendFeedLines([{
                        time: getFeedTime(completionLineIndex),
                        text: "Saved analysis loaded - ready to review.",
                        warn: false,
                    }]);
                    await Promise.all([
                        waitForMinimumTrackingDuration(trackingStartedAt),
                        waitForFeedPlayback(),
                    ]);
                    setAnalysis(normalized);
                    setAgentState("resolved");
                    setWorkflowOpen(false);
                    return;
                }

                const warningLineIndex = feedLineCountRef.current;
                appendFeedLines([{
                    time: getFeedTime(warningLineIndex),
                    text: "Saved analysis was found, but it could not be used. Other agent APIs were skipped.",
                    warn: true,
                }]);
                await Promise.all([
                    waitForMinimumTrackingDuration(trackingStartedAt),
                    waitForFeedPlayback(),
                ]);
                setAnalysisError("A saved analysis exists for this ticket but is incomplete or unsuccessful. No new agent run was started.");
                setAgentState("resolved");
                setWorkflowOpen(false);
                return;
            }
        } catch (error) {
            console.error("Could not load saved PO mismatch analysis:", error);
        } finally {
            setIsCheckingSavedAnalysis(false);
        }

        await runAgent(trackingStartedAt);
    };

    return <>
        <div className={`po-mismatch__agent-layout po-mismatch__agent-layout--full${agentState === "resolved" || agentState === "sent" ? " po-mismatch__agent-layout--resolved" : ""}`}>
            <main className="po-mismatch__agent-main">
                <div className={`po-mismatch__ticket-header${agentState === "resolved" || agentState === "sent" ? " po-mismatch__ticket-header--resolved" : ""}`}><div className="po-mismatch__ticket-header-top"><div><h3>{analysis?.documentReferences.ticket_id || selectedTicket.id} · {analysis?.documentReferences.supplier || selectedTicket.supplier}</h3><p className="po-mismatch__ticket-summary">{agentState === "inactive" ? selectedTicket.summary : analysis?.documentReferences.invoice_number ? `Invoice ${analysis.documentReferences.invoice_number} mismatch on ${analysis.documentReferences.po_number || "purchase order"}` : "Invoice INV-88231 price mismatch on PO 4500099114"}</p></div><span className={`po-mismatch__priority po-mismatch__priority--${(analysis?.documentReferences.priority || selectedTicket.priority).toLowerCase()}`}>{(analysis?.documentReferences.priority || selectedTicket.priority).toUpperCase()} PRIORITY</span></div>{agentState === "inactive" && <div className="po-mismatch__ticket-stats"><div><span className="po-mismatch__stat-label">Blocked value</span><strong>{selectedTicket.value}</strong></div><div><span className="po-mismatch__stat-label">Raised time</span><strong>{selectedTicket.raised}</strong></div><div><span className="po-mismatch__stat-label">Category</span><strong>{selectedTicket.category}</strong></div></div>}</div>
                {agentState === "inactive" && <section className="po-mismatch__panel po-mismatch__agent-inactive">
                    <div className="po-mismatch__agent-inactive-icon">
                        <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="4" y="4" width="20" height="20" rx="4" stroke="#9ca3af" stroke-width="1.8"></rect><path d="M9 14h10M14 9v10" stroke="#9ca3af" stroke-width="1.8" stroke-linecap="round"></path></svg>
                    </div>
                    <p>Click "Run Mismatch Agent" above to cross-reference line item prices with contract agreements.</p>
                    <p className="initialize">Initialize AI diagnostic workflow to fetch historical Ariba contract metrics &amp; PO states.</p><button type="button" className="btn btn--gradient" disabled={isAnalysisLoading || isCheckingSavedAnalysis} onClick={handleRunAgent}>{isCheckingSavedAnalysis ? "Checking saved analysis..." : isAnalysisLoading ? "Running Mismatch Agent..." : "Run Mismatch Agent"}</button></section>}
                {agentState === "running" && <section className="po-mismatch__panel po-mismatch__evidence"><div className="po-mismatch__evidence-head"><h3><span className="po-mismatch__live-dot" /> Live evidence feed</h3><span className={isPolling ? "po-mismatch__polling-status" : ""}><svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ animation: "1.5s linear 0s infinite normal none running glow-rotate" }}><circle cx="7" cy="7" r="5.5" stroke="#a855f7" strokeWidth="1.2"></circle><path d="M7 4v3l2 2" stroke="#a855f7" strokeWidth="1.2" strokeLinecap="round"></path></svg>{isPolling ? "Agent working..." : "Agent working..."}</span></div>{isPolling && <div className="po-mismatch__polling-indicator" role="status"><span className="po-mismatch__polling-spinner" /> Waiting for Agent response...</div>}{feedLines.slice(0, visibleFeedLineCount).map((line, index) => <div className="po-mismatch__evidence-row" key={`${line.text}-${index}`}><time>{line.time}</time><p className={line.warn ? "is-warning" : ""}>{line.warn && "!  "}{line.text}</p></div>)}
                <div className="po-mismatch__evidence-footer">
                    <button type="button" className="po-mismatch__cancel" onClick={() => setAgentState("inactive")}>Cancel Agent Run</button>
                </div>
                </section>}
                {agentState === "resolved" && analysisError && <p className="po-mismatch__analysis-error" role="status">{analysisError}</p>}
                {(agentState === "resolved" || agentState === "sent") && <ResolvedContent analysis={analysis} agentState={agentState} selectedTicket={selectedTicket} onSend={() => setAgentState("sent")} onBackToQueue={onBackToQueue} onViewInsights={onViewInsights} />}
            </main>
            {(agentState !== "resolved" && agentState !== "sent") || workflowOpen ? <aside className="po-mismatch__agent-side">
                <div className="po-mismatch__workflow">
                    <h4 className="po-mismatch__side-title">Agent workflow</h4>
                    {WORKFLOW_STEPS.map((step, index) => { 
                        const status = workflowStatus(index);
                         return <div key={step.label} className="po-mismatch__workflow-step">
                        <span className={`po-mismatch__workflow-step-icon po-mismatch__workflow-step-icon--${status}`}>
                            <div className="size-1.5 rounded-full bg-[#d1d5db]"></div>
                            {status === "done" && <Check size={12} />}
                        </span>
                        <div>
                            <strong>{step.label}</strong>
                            <span>{status === "active" ? step.running : status === "done" ? step.done : "Queued"}</span>
                        </div>
                        </div>;
                     })}
                     </div>
                     <div className="po-mismatch__guardrails">
                        <h4 className="po-mismatch__side-title">Guardrails</h4>
                        <dl>
                            <div>
                                <dt>Confidence threshold</dt>
                                <dd>Resolution proposed only above 90%</dd>
                            </div>
                            <div>
                                <dt>Write access</dt>
                                <dd>None - agent drafts, human posts</dd>
                                </div><div><dt>Contract precedence</dt>
                                <dd>Contract price always wins over invoice</dd>
                            </div>
                            <div>
                                <dt>Audit</dt>
                                <dd>Every field, source and timestamp logged</dd>
                            </div>
                            </dl>
                            </div>
                            </aside> : null
                            }
        </div>
    </>;
}

function ResolvedContent({ analysis, agentState, selectedTicket, onSend, onBackToQueue, onViewInsights }: { analysis: MismatchAnalysis | null; agentState: AgentState; selectedTicket: Ticket; onSend: () => void; onBackToQueue: () => void; onViewInsights: () => void }) {
    if (agentState === "sent") return <div className="po-mismatch__resolved-stack"><section className="po-mismatch__success"><CheckCircle2 size={22} /><div><h3>Resolution Sent to Supplier</h3><p>Credit memo request for {selectedTicket.value} sent to {selectedTicket.supplier} via Ariba. Ticket {selectedTicket.id} moved to resolved.</p></div><time>Today, 08:12:45</time></section><section className="po-mismatch__panel"><h3 className="po-mismatch__section-title">Resolution summary</h3>{[["Action Taken:", "Credit memo requested - over-payment returned"], ["PO Correction:", "Unit price on PO line 1 updated to $43.00 (contract rate)"], ["Supplier Notified:", `Ariba message sent to ${selectedTicket.supplier}, 08:12:45`], ["Audit Trail:", "All changes logged with source documents attached"], ["Next Review:", "Auto-monitor next 3 invoices from this supplier for price reversion"]].map(([label, value]) => <div className="po-mismatch__summary-row" key={label}><span>{label}</span><strong>{value}</strong></div>)}</section><section className="po-mismatch__whats-next"><h3>What&apos;s next</h3><div className="po-mismatch__next-cards"><div className="po-mismatch__next-card"><div><h4>Return to Queue</h4><p>There are 2 more mismatch tickets waiting for resolution in your queue.</p></div><button type="button" onClick={onBackToQueue}>Back to Queue →</button></div><div className="po-mismatch__next-card"><div><h4>View Insights</h4><p>See performance patterns across all 6 mismatch archetypes this quarter.</p></div><button type="button" onClick={onViewInsights}>Ticket Insights →</button></div></div><div className="po-mismatch__prevention-note"><span aria-hidden="true">ⓘ</span><p><strong>Prevention Note:</strong> This supplier has now triggered 2 price mismatches this quarter. Consider flagging for catalogue re-sync in the next review cycle.</p></div></section></div>;
    const comparisonRows = getComparisonRows(analysis?.dataTable || []);
    return <div className="po-mismatch__resolved-stack"><section className="po-mismatch__recommendation"><div className="po-mismatch__recommendation-head"><h3>✦ Recommended resolution</h3><span>{analysis?.severity || "96% CONFIDENCE"}</span></div><h4>{analysis?.overallRecommendation || "Accept and return over-payment of $3,690.00, then correct unit price on PO line 1 to match contract CTR-2211."}</h4><p>{analysis ? `Financial exposure: ${formatExposure(analysis.exposure)} · ${analysis.recommendations.length} line items require resolution.` : "Based on: contract CTR-2211 specifies $43.00/unit; invoice charges $45.50/unit across 1,520 qty; 3 prior invoices from this supplier used $43.00 correctly."}</p><div><button type="button" className="po-mismatch__secondary-action">Escalate to Human</button><button type="button" className="btn btn--gradient" onClick={onSend}>Accept &amp; Send to Supplier →</button></div></section><section className="po-mismatch__panel po-mismatch__comparison"><h3 className="po-mismatch__section-title">Line-level comparison - PO 4500099114 line 10 vs INV-88231</h3><table><thead><tr><th>Attribute</th><th>Purchase order / contract</th><th>Supplier invoice</th><th>Result</th></tr></thead><tbody>{comparisonRows.map((row, index) => <tr key={`${row[0]}-${index}`} className={row[4] === "match" ? "" : "is-over"}><td>{row[0]}</td><td>{row[1]}</td><td>{row[2]}</td><td className={`is-${row[4]}`}>{row[3]}</td></tr>)}</tbody></table></section><div className="po-mismatch__resolution-cards"><ResolutionCard label="Mismatch drivers" value={analysis ? String(analysis.recommendations.length) : "1"} detail={analysis ? `${analysis.recommendations.length} line items` : "unit price"} tone="orange" /><ResolutionCard label="Financial exposure" value={analysis ? formatExposure(analysis.exposure) : "$1,404.00"} detail="from API analysis" tone="orange" /><ResolutionCard label="Overall severity" value={analysis?.severity || "CRITICAL"} detail="analysis result" tone="blue" /><ResolutionCard label="Diagnosis confidence" value={analysis ? "API" : "96%"} detail={analysis ? "agent response" : "auto-resolvable"} tone="green" /></div><section className="po-mismatch__callout po-mismatch__diagnosis"><div className="po-mismatch__callout-head"><span className="po-mismatch__ai-badge">✦ AI</span><span className="po-mismatch__callout-label">What actually went wrong</span></div><p>{analysis?.summary || "Quantities and receipts agree. The single driver is unit price: the supplier billed $42.50 against a contracted and PO'd $39.80. Contract CTR-2211 fixes that price until 31 Dec 2026, and the same supplier billed the correct price on the two previous invoices - this looks like a price-list version error on their side, not a renegotiation."}</p></section><div className="po-mismatch__resolution-options"><ResolutionOption label="Recommended" confidence="96%" tone="good" text={analysis?.recommendations[0]?.recommendation || "Reject the invoice line and request a corrected invoice at the contract price of $39.80"} /><ResolutionOption label="Alternative" confidence="81%" tone="warn" text={analysis?.recommendations[1]?.recommendation || "Accept and short-pay $20,696.00, then book a $1,404.00 debit memo against the supplier"} /><ResolutionOption label="Not advised" confidence="12%" tone="bad" text={analysis?.recommendations[2]?.recommendation || "Raise a PO change order to $42.50 - this would breach the fixed contract price"} /></div><section className="po-mismatch__panel po-mismatch__draft-panel"><h3 className="po-mismatch__section-title"><span className="po-mismatch__draft-icon">○</span> AI drafted ticket resolution - ready for the helpdesk agent</h3><div className="po-mismatch__draft-box"><p className="po-mismatch__draft">{analysis?.recommendations[0]?.recommendation || "Invoice INV-88231 is blocked by a unit-price mismatch on line 10. The PO and contract CTR-2211 both carry $39.80 per unit; the invoice was submitted at $42.50, over-billing by $1,404.00. Quantities and the goods receipt match in full. Action: reject the invoice line in Ariba with reason code PRICE_VARIANCE and request a corrected invoice. No PO change is required."}</p></div><p className="po-mismatch__readonly">Read-only Ariba pull - no auto-posting of financial documents</p></section></div>;
}

function ResolutionOption({ label, confidence, tone, text }: { label: string; confidence: string; tone: string; text: string }) {
    return <div className="po-mismatch__resolution-option"><div><span className={`po-mismatch__resolution-tag po-mismatch__resolution-tag--${tone}`}>{label}</span><strong>{confidence}</strong></div><p>{text}</p></div>;
}

function ResolutionCard({ label, value, detail, tone }: { label: string; value: string; detail: string; tone: string }) {
    return <div className={`po-mismatch__resolution-card po-mismatch__resolution-card--${tone}`}><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>;
}
